// @falko/frontend — point d'entrée React
// Monte l'application Falko sur l'élément <div id="root"> de index.html.

// ⚠️ Import Sentry EN PREMIER (avant React + App), pour qu'il puisse instrumenter
// le runtime au plus tôt et capturer les erreurs dès le chargement.
import './instrument';

import { StrictMode, Component, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { App } from './App.js';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Élément #root introuvable dans index.html');
}

// ErrorBoundary custom (compatible React 19) qui capture les erreurs de render
// et les envoie à Sentry via captureException, en plus de window.onerror que
// Sentry.init() intercepte déjà tout seul.
interface ErrorBoundaryState {
  hasError: boolean;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="text-5xl mb-4">😵</div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">Oups, une erreur</h1>
            <p className="text-neutral-600 mb-6">
              Falko a rencontré une erreur inattendue. Nos équipes ont été notifiées et
              regardent ce qui s'est passé.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2.5 rounded-lg font-medium transition"
            >
              Recharger la page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </StrictMode>,
);
